/**
 * Layout recipes — semantic page shells for StudyHub «Картотека».
 *
 * Source of truth for values: packages/ui/src/styles/tokens.css (L2 semantic).
 * Tailwind utilities (px-page-x, space-y-section, max-w-list, …) are wired via
 * @theme in apps/{web,admin}/src/styles.css.
 *
 * Rule: list/detail page roots MUST use these recipes (or CSS vars they wrap).
 * Do not copy `mx-auto max-w-7xl px-4 py-6 lg:px-6` by hand.
 */

/** List / catalogue pages: SectionHeader → toolbar → grid|list */
export const LIST_PAGE_SHELL =
	"mx-auto max-w-list space-y-section px-page-x py-page-y lg:px-page-x-lg";

/** Home / dashboard (wider vertical rhythm than list section gap). */
export const DASHBOARD_PAGE_SHELL =
	"mx-auto flex w-full max-w-list flex-col gap-7 px-page-x py-page-y lg:px-page-x-lg";

/** Top chrome bar inside overlay/fullscreen pages (offline, consent). */
export const PAGE_CHROME_BAR =
	"mx-auto flex h-full max-w-list items-center justify-between px-page-x lg:px-page-x-lg";

/** Fit-to-viewport detail (visuals desktop split, similar workbenches). */
export const FIT_APP_PAGE_SHELL =
	"mx-auto flex h-fit-app max-w-list flex-col gap-cluster overflow-hidden px-page-x py-page-y lg:px-page-x-lg";

/**
 * Header → toolbar gap when the page root cannot use `space-y-*`
 * (e.g. PullToRefresh owns the outer wrapper). Prefer LIST_PAGE_SHELL.
 */
export const LIST_PAGE_HEADER_SPACING = "mb-section";

/** Canonical entity card grid (1 → 2 → 3). Materials / collections / favorites. */
export const LIST_PAGE_GRID =
	"grid auto-rows-fr grid-cols-1 gap-grid sm:grid-cols-2 lg:grid-cols-3";

/**
 * @deprecated Alias of LIST_PAGE_GRID (locked 2A: 3 cols, no xl:4).
 * Kept so older imports keep compiling; prefer LIST_PAGE_GRID.
 */
export const LIST_PAGE_GRID_WIDE = LIST_PAGE_GRID;

/**
 * Плотная ВИТРИНА карточек: 1 → 2 → 3 → 4 колонки.
 *
 * НЕ «широкая версия» `LIST_PAGE_GRID` и не замена ему: канон списка сущностей
 * остаётся трёхколоночным (locked 2A), потому что карточка материала/конспекта
 * несёт мету, прогресс и главное действие — в четверти ширины они не читаются.
 *
 * Эта решётка — для витрин, где карточка сведена к обложке, названию и паре
 * чипов (каталог курсов): там четвёртая колонка сокращает прокрутку, а не
 * ломает чтение.
 *
 * Пороги считаются по СОБСТВЕННОЙ ширине решётки (`@container/cards`), а не по
 * ширине окна. Вьюпортные `lg:`/`xl:` здесь врали: боковое меню слева и панель
 * репетитора справа отжимают центральную колонку до ~800px, окно при этом
 * остаётся ≥1280 — и каталог продолжал держать четыре колонки по ~180px.
 * Контейнерные пороги (512 / 768 / 1024px) держат карточку не уже ~240px в
 * любой комбинации открытых панелей. Контейнер ИМЕНОВАННЫЙ — безымянные
 * `@`-запросы внутри карточек должны и дальше мерить свою карточку.
 */
export const LIST_PAGE_GRID_DENSE =
	"grid auto-rows-fr grid-cols-1 gap-grid " +
	"@lg/cards:grid-cols-2 @3xl/cards:grid-cols-3 @5xl/cards:grid-cols-4";

/**
 * Обёртка витрины — объявляет контейнер, по которому меряет пороги
 * `LIST_PAGE_GRID_DENSE`. Отдельный элемент обязателен: container query читает
 * ближайшего ПРЕДКА-контейнер, на самом себе он не срабатывает, и решётка с
 * `@container` в собственном className молча осталась бы одноколоночной.
 */
export const LIST_PAGE_GRID_DENSE_SHELL = "@container/cards";

/** Study plan cards — wider cards, max 2 columns. */
export const LIST_PAGE_GRID_COMPACT =
	"grid auto-rows-fr grid-cols-1 gap-grid sm:grid-cols-2 lg:grid-cols-2";

/** Dense session/skill rows. */
export const LIST_PAGE_STACK = "flex flex-col gap-stack";

/**
 * Full-bleed detail (collection / study-plan) under MainLayout.
 * Без space-y — вертикальный ритм задаёт caller / DetailPageContainer.
 */
export const DETAIL_PAGE_SHELL = "mx-auto w-full px-page-x py-page-y lg:px-page-x-lg";

/**
 * Wide workbench (material workspace).
 * Без space-y — desktop compose’ит `flex … gap-4`, mobile — `space-y-4`.
 */
export const WORKSPACE_PAGE_SHELL = "mx-auto max-w-workspace px-page-x py-4 lg:px-page-x-lg";

/** Narrow marketing column (FAQ). Prefer this over cn(MARKETING_PAGE_SHELL, "max-w-narrow"). */
export const MARKETING_NARROW_SECTION = "mx-auto max-w-narrow px-page-x-lg py-12 lg:py-16";

/** Reading / editor letter column. */
export const READING_PAGE_SHELL = "mx-auto max-w-reading px-page-x py-page-y lg:px-page-x-lg";

/** Mid-width forms (quiz edit, subsection). */
export const FORM_PAGE_SHELL =
	"mx-auto max-w-form space-y-section px-page-x py-page-y lg:px-page-x-lg";

/**
 * Narrow reading / legal / about / empty-error states.
 * Use instead of hand-rolled `max-w-3xl px-4 py-12 lg:px-6`.
 */
export const NARROW_PAGE_SHELL = "mx-auto w-full max-w-narrow px-page-x py-page-y lg:px-page-x-lg";

/** Centered empty/error/status block (taller vertical pad). */
export const STATUS_PAGE_SHELL = "mx-auto max-w-narrow px-page-x py-12 lg:px-page-x-lg";

/** Detail content + fixed aside: `grid … lg:grid-cols-[1fr_var(--rail-width)]`. */
export const DETAIL_WITH_RAIL =
	"grid grid-cols-1 gap-5 lg:grid-cols-[1fr_var(--rail-width)] lg:items-start";

/** Entity list row surface padding (use with ROW_BASE or equivalent). */
export const ENTITY_PAD_ROW = "p-pad-row";

/** Entity grid card surface padding. */
export const ENTITY_PAD_CARD = "p-pad-card";

/** ElevatedPanel body padding (content panels). */
export const PANEL_PAD = "p-pad-panel lg:p-pad-panel-lg";

/** ElevatedPanel chrome / dense header / player chrome. */
export const PANEL_PAD_CHROME = "p-pad-card";

/** Detail / collection / workspace aside width (canon 320). */
export const DETAIL_RAIL = "w-rail";

/** Узкий filter/nav-рельс (папки Библиотеки) — 240, уже detail-rail и sidebar. */
export const LIST_RAIL = "w-rail-compact";

/**
 * Marketing / landing sections (desktop-first px-6 rhythm).
 * Not an app list shell — different vertical section spacing.
 */
export const MARKETING_PAGE_SHELL = "mx-auto max-w-list px-page-x-lg";

/** Marketing content section vertical rhythm. */
export const MARKETING_SECTION = "mx-auto max-w-list px-page-x-lg py-12 lg:py-16";

/**
 * Вертикальный ритм секции, когда оболочку (`max-w` + горизонтальный pad)
 * уже дал `MARKETING_PAGE_SHELL`. Вторая копия `mx-auto max-w-list px-*`
 * внутри колонки рядом с рельсом сжимала бы контент дважды.
 */
export const MARKETING_SECTION_STACK = "py-10 lg:py-12";
